import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FileSearch, X, Mic, FileUp } from 'lucide-react';
import { RehearsalDialog } from './RehearsalDialog';
import { ImportDocumentDialog } from './ImportDocumentDialog';
import { BriefDialog } from './BriefDialog';
import { PenLine, CalendarClock, DollarSign, Files, Scale } from 'lucide-react';
import { TrialSimDialog } from './TrialSimDialog';
import { DeadlinesDialog } from './DeadlinesDialog';
import { PayPalChargeDialog } from './PayPalChargeDialog';
import { MatterVoiceTools } from './MatterVoiceTools';
import { MatterDocumentsDialog } from './MatterDocumentsDialog';

export const LinkedCauses = ({ matter, onUpdated }) => {
  const { api } = useAuth();
  const causes = matter.linked_causes || [];
  if (!causes.length) return null;
  const unlink = async (rit) => {
    try {
      await api.delete(`/crm/matters/${matter.id}/link-cause`, { params: { rit } });
      toast.success(`Causa ${rit} desvinculada`);
      onUpdated();
    } catch {
      toast.error('No se pudo desvincular');
    }
  };
  return (
    <div data-testid={`matter-linked-causes-${matter.id}`} className="mt-2 flex flex-wrap gap-1.5">
      {causes.map((c) => (
        <span key={c.rit} className="text-[11px] bg-slate-800 border border-slate-700 rounded-full pl-2 pr-1 py-0.5 text-slate-300 flex items-center gap-1" title={`${c.labeled || ''} · ${c.court || ''}`}>
          <FileSearch className="w-3 h-3 text-cyan-400" />{c.rit}
          <button onClick={() => unlink(c.rit)} aria-label="Desvincular" className="text-slate-500 hover:text-red-400"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
};

export const MatterExtraActions = ({ matter, onUpdated }) => {
  const [rehearse, setRehearse] = useState(false);
  const [trial, setTrial] = useState(false);
  const [importing, setImporting] = useState(false);
  const [briefing, setBriefing] = useState(false);
  const [deadlines, setDeadlines] = useState(false);
  const [charging, setCharging] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const high = (matter.documents || []).filter((d) => d.classification?.urgency === 'alta').length;
  const btn = 'flex-1 text-xs font-semibold rounded-lg py-2 flex items-center justify-center gap-1.5 border transition-colors';
  return (
    <>
      <div className="mt-2 flex gap-2">
        <button data-testid={`matter-rehearsal-btn-${matter.id}`} onClick={() => setRehearse(true)} className={`${btn} border-purple-500/40 text-purple-300 hover:bg-purple-500/10`}>
          <Mic className="w-3.5 h-3.5" />Ensayar alegato
        </button>
        <button data-testid={`matter-import-doc-btn-${matter.id}`} onClick={() => setImporting(true)} className={`${btn} border-slate-600 text-slate-300 hover:bg-slate-800`}>
          <FileUp className="w-3.5 h-3.5" />Importar documento
        </button>
      </div>
      <button data-testid={`matter-trial-sim-btn-${matter.id}`} onClick={() => setTrial(true)} className={`${btn} w-full mt-2 border-amber-500/40 text-amber-300 hover:bg-amber-500/10`}>
        <Scale className="w-3.5 h-3.5" />Simular juicio completo (etapa por etapa)
      </button>
      <TrialSimDialog matter={matter} open={trial} onOpenChange={setTrial} />
      <button data-testid={`matter-brief-btn-${matter.id}`} onClick={() => setBriefing(true)} className={`${btn} w-full mt-2 border-blue-500/40 text-blue-300 hover:bg-blue-500/10`}>
        <PenLine className="w-3.5 h-3.5" />{matter.legal_brief ? 'Ver escrito redactado' : 'Redactar escrito (descargos, recurso...)'}
      </button>
      <button data-testid={`matter-deadlines-btn-${matter.id}`} onClick={() => setDeadlines(true)} className={`${btn} w-full mt-2 border-amber-500/40 text-amber-300 hover:bg-amber-500/10`}>
        <CalendarClock className="w-3.5 h-3.5" />Plazos{matter.deadlines?.length ? ` (${matter.deadlines.length})` : ''}
      </button>
      <button data-testid={`matter-paypal-btn-${matter.id}`} onClick={() => setCharging(true)} className={`${btn} w-full mt-2 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10`}>
        <DollarSign className="w-3.5 h-3.5" />Cobrar con PayPal
      </button>
      <button data-testid={`matter-documents-btn-${matter.id}`} onClick={() => setDocsOpen(true)} className={`${btn} w-full mt-2 border-slate-600 text-slate-300 hover:bg-slate-800`}>
        <Files className="w-3.5 h-3.5" />Documentos por urgencia ({matter.documents?.length || 0}){high ? <span className="ml-1 text-red-300">· {high} alta</span> : null}
      </button>
      {docsOpen && <MatterDocumentsDialog matter={matter} open={docsOpen} onOpenChange={setDocsOpen} onUpdated={onUpdated} />}
      <MatterVoiceTools matter={matter} onUpdated={onUpdated} />
      {charging && <PayPalChargeDialog matter={matter} open={charging} onOpenChange={setCharging} />}
      {deadlines && <DeadlinesDialog matter={matter} open={deadlines} onOpenChange={setDeadlines} onUpdated={onUpdated} />}
      {briefing && <BriefDialog matter={matter} open={briefing} onOpenChange={setBriefing} onUpdated={onUpdated} />}
      {rehearse && <RehearsalDialog matter={matter} open={rehearse} onOpenChange={setRehearse} onUpdated={onUpdated} />}
      {importing && <ImportDocumentDialog matter={matter} open={importing} onOpenChange={setImporting} onUpdated={onUpdated} />}
    </>
  );
};
